import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatBitcoin(btcValue: number | undefined): string {
  // Format BTC with 8 decimal places and thousands separators
  return (btcValue ?? 0).toLocaleString('en-US', {
    minimumFractionDigits: 8,
    maximumFractionDigits: 8
  });
}

export function formatDate(timestamp: number, format?: string): string {
  const date = new Date(timestamp * 1000);
  
  // If a specific format is provided, use it
  if (format) {
    // Simple format string replacement
    // MM/DD/YYYY, HH:mm:ss - example: 01/12/2025, 00:13:29
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    };
    
    return date.toLocaleString('en-US', options);
  }
  
  // Default format (backward compatible)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
}

export function formatDateRange(startDate: string | null, endDate: string | null): string {
  if (!startDate && !endDate) {
    return "All time";
  }
  
  const formatDateStr = (dateStr: string) => {
    try {
      // Split the YYYY-MM-DD format and create a UTC date object
      // This ensures consistent display regardless of timezone
      const [year, month, day] = dateStr.split('-').map(Number);
      
      // Create the display date with the exact day specified (not timezone adjusted)
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[month-1]} ${day}, ${year}`;
    } catch (error) {
      console.error(`Error formatting date: ${dateStr}`, error);
      
      // Fallback to standard formatting
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC' // Force UTC timezone
        });
      } catch (e) {
        return "Invalid date";
      }
    }
  };
  
  // Handle different date formats but ensure we're working with YYYY-MM-DD
  const safeFormat = (dateStr: string | null) => {
    if (!dateStr) return null;
    
    // If already in YYYY-MM-DD format, use it directly
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return formatDateStr(dateStr);
    }
    
    // Otherwise try to normalize it
    try {
      // Create date and force output as YYYY-MM-DD in UTC
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        console.error(`Invalid date: ${dateStr}`);
        return "Invalid date";
      }
      
      // Format to YYYY-MM-DD in UTC
      const utcYear = date.getUTCFullYear();
      const utcMonth = (date.getUTCMonth() + 1).toString().padStart(2, '0');
      const utcDay = date.getUTCDate().toString().padStart(2, '0');
      const normalizedDate = `${utcYear}-${utcMonth}-${utcDay}`;
      
      return formatDateStr(normalizedDate);
    } catch (error) {
      console.error(`Error normalizing date: ${dateStr}`, error);
      return "Invalid date";
    }
  };
  
  if (startDate && endDate) {
    const formattedStart = safeFormat(startDate);
    const formattedEnd = safeFormat(endDate);
    if (formattedStart && formattedEnd) {
      return `${formattedStart} - ${formattedEnd}`;
    } else if (formattedStart) {
      return `From ${formattedStart}`;
    } else if (formattedEnd) {
      return `Until ${formattedEnd}`;
    }
    return "Date range";
  } else if (startDate) {
    const formattedStart = safeFormat(startDate);
    return formattedStart ? `From ${formattedStart}` : "From date";
  } else if (endDate) {
    const formattedEnd = safeFormat(endDate);
    return formattedEnd ? `Until ${formattedEnd}` : "Until date";
  }
  
  // Fallback
  return "Custom date range";
}

export function dateToUnixTimestamp(dateStr: string | null): number | null {
  if (!dateStr) return null;
  
  // The date string is in YYYY-MM-DD format (e.g., "2022-12-01")
  // We need to ensure it's treated as UTC to avoid timezone issues
  
  try {
    // Parse the date in a way that enforces UTC
    const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
    
    // Create a date at UTC midnight for the specified day
    // Month is 0-indexed in JavaScript Date
    const utcDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
    
    // Return the Unix timestamp in seconds
    return Math.floor(utcDate.getTime() / 1000);
  } catch (error) {
    console.error(`Error converting date ${dateStr} to timestamp:`, error);
    return Math.floor(new Date(dateStr).getTime() / 1000); // Fallback to original behavior
  }
}

export function formatPhoneNumber(value: string): string {
  if (!value) return value;
  
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/[^\d]/g, '');
  
  // Handle different phone number lengths
  const phoneLength = phoneNumber.length;
  
  if (phoneLength < 4) return phoneNumber;
  if (phoneLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  if (phoneLength < 11) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }
  if (phoneLength === 11 && phoneNumber[0] === '1') {
    return `+1 (${phoneNumber.slice(1, 4)}) ${phoneNumber.slice(4, 7)}-${phoneNumber.slice(7, 11)}`;
  }
  if (phoneLength > 10) {
    return `+${phoneNumber.slice(0, phoneLength - 10)} (${phoneNumber.slice(phoneLength - 10, phoneLength - 7)}) ${phoneNumber.slice(phoneLength - 7, phoneLength - 4)}-${phoneNumber.slice(phoneLength - 4)}`;
  }
  
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
}

export function stripPhoneNumber(value: string): string {
  if (!value) return '';
  
  // Remove all non-numeric characters and ensure it starts with country code
  const phoneNumber = value.replace(/[^\d]/g, '');
  
  // If it's a 10-digit US number, add +1 prefix
  if (phoneNumber.length === 10) {
    return `+1${phoneNumber}`;
  }
  
  // If it's an 11-digit number starting with 1, add + prefix
  if (phoneNumber.length === 11 && phoneNumber[0] === '1') {
    return `+${phoneNumber}`;
  }
  
  // For other lengths, add + prefix if not empty
  if (phoneNumber.length > 0) {
    return `+${phoneNumber}`;
  }
  
  return '';
}

export function validatePhoneNumber(value: string): boolean {
  if (!value) return true; // Allow empty values
  
  const stripped = stripPhoneNumber(value);
  
  // Must start with + and have at least 10 digits total (including country code)
  const phoneRegex = /^\+\d{10,15}$/;
  return phoneRegex.test(stripped);
}

export function truncateHash(hash: string, length: number = 16): string {
  if (!hash || hash.length <= length + 3) {
    return hash;
  }
  return hash.substring(0, length) + '...';
}
